import { useCallback, useEffect, useState } from 'react'

type SidebarMode = 'auto' | 'expanded' | 'collapsed'

export const AUTO_COLLAPSE_BELOW_PX = 1280

const readIsNarrow = (thresholdPx: number): boolean => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < thresholdPx
}

const useViewportNarrow = (thresholdPx: number): boolean => {
  const [isNarrow, setIsNarrow] = useState<boolean>(() => readIsNarrow(thresholdPx))

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const update = () => setIsNarrow(readIsNarrow(thresholdPx))
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [thresholdPx])

  return isNarrow
}

export const useSidebarCollapsedPersistence = (): [boolean, (next: boolean) => void] => {
  // In-memory only — sidebar mode resets to `auto` (viewport-driven) on every
  // page load. Previously persisted to localStorage; intentionally removed so
  // the sidebar state isn't sticky across refreshes.
  const [mode, setMode] = useState<SidebarMode>('auto')

  const isNarrow = useViewportNarrow(AUTO_COLLAPSE_BELOW_PX)

  // TDS only exposes a binary toggle; clicking it leaves `auto` and pins the
  // user's explicit preference for the rest of the session.
  const setCollapsed = useCallback((next: boolean) => {
    setMode(next ? 'collapsed' : 'expanded')
  }, [])

  const isCollapsed = mode === 'auto' ? isNarrow : mode === 'collapsed'

  return [isCollapsed, setCollapsed]
}
